import { useTranslation } from 'react-i18next';
import { useToast } from '../utils/toastUtils';
import { extractIdFromUrl } from '../utils/urlUtils';

/**
 * Custom hook for copying counter URL to clipboard
 */
export function useCopyUrl() {
  const { t } = useTranslation();
  const { showToast } = useToast();

  /**
   * Copy a counter URL to the clipboard
   * @param counterId The counter ID or owner URL
   * @param isOwnerUrl Whether this is an owner URL
   */
  const copyUrl = (counterId: string, isOwnerUrl = false) => {
    // Extract just the ID part
    const cleanId = extractIdFromUrl(counterId);
    
    // Build URL from base location and counterId
    const baseUrl = window.location.origin;
    const counterUrl = `${baseUrl}/${cleanId}`;
    
    navigator.clipboard.writeText(counterUrl)
      .then(() => {
        showToast(t('common.copied', 'URL copied to clipboard'), 'success');
      })
      .catch((error) => {
        console.error('Failed to copy URL:', error);
        showToast(t('common.copyFailed', 'Failed to copy URL'), 'danger');
      });
  };

  /**
   * Get a formatted counter URL
   * @param counterId The counter ID or owner URL 
   * @param isOwnerUrl Whether this is an owner URL
   * @returns The complete counter URL
   */
  const getCounterUrl = (counterId: string, isOwnerUrl = false): string => {
    // Extract just the ID part
    const cleanId = extractIdFromUrl(counterId);
    
    const baseUrl = window.location.origin;
    return `${baseUrl}/${cleanId}`;
  };

  /**
   * Get a formatted public URL for a counter ID
   * @param counterId The counter ID
   * @returns The complete public URL
   */
  const getPublicUrl = (counterId: string): string => {
    return getCounterUrl(extractIdFromUrl(counterId), false);
  };

  /**
   * Get a formatted owner URL for a counter
   * @param ownerUrl The counter's owner URL
   * @returns The complete owner URL
   */
  const getOwnerUrl = (ownerUrl: string): string => {
    return getCounterUrl(extractIdFromUrl(ownerUrl), true);
  };

  return {
    copyUrl,
    getCounterUrl,
    getPublicUrl,
    getOwnerUrl
  };
} 